import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { FormatCurrencyPipe } from '@app/shared/pipes/format-currency.pipe';
import { SpinnerService } from '@app/shared/spinner.service';
import { parseTz } from '@app/shared/time';
import { SafeBalance, StationBalance, StrongboxCountView, countStamp, lostCause, lostReply, nonNegativeCents, refusalText, signedMoney, sumCents } from '../reglement.models';
import { ReglementService } from '../reglement.service';
import { CashCountGridComponent } from '../cash-count-grid/cash-count-grid.component';
import { CountedAmount } from '../cash-count-grid/counted-amount';
import { CentsInputDirective } from '../cents-input.directive';

// Coffre de la carte « Coffres-forts » : nom et magasin ; son dernier inventaire se relit à l'ouverture, avec le solde.
export interface StrongboxCountDialogData {
	strongbox: SafeBalance;
}

@Component({
	selector: 'app-strongbox-count-dialog',
	standalone: true,
	templateUrl: './strongbox-count-dialog.component.html',
	styleUrls: ['./strongbox-count-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ReactiveFormsModule, MatDialogModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, FormatCurrencyPipe, CashCountGridComponent, CentsInputDirective],
})
export class StrongboxCountDialogComponent implements OnInit {

	private reglementService = inject(ReglementService);
	private spin = inject(SpinnerService);
	private destroyRef = inject(DestroyRef);
	public dialogRef = inject(MatDialogRef<StrongboxCountDialogComponent, StrongboxCountView | null>);
	public data: StrongboxCountDialogData = inject(MAT_DIALOG_DATA);
	private fc = new FormatCurrencyPipe();

	// Solde théorique à l'ouverture de la boîte, pour l'écart en direct ; le serveur fige le sien sous le verrou du coffre.
	public balance: StationBalance | null = null;
	// Dernier inventaire relu avec le solde (celui de la carte peut dater d'avant un inventaire fait ailleurs) : son auteur, et sa date à défaut d'anchor_start.
	private listed: Pick<SafeBalance, 'last_count_at' | 'last_count_user_name'> | null = null;
	public loadError = '';
	public sendError = '';
	public sending = false;
	// Réponse perdue : inventaire peut-être enregistré ; la boîte ne renvoie plus rien (un doublon ferait un second inventaire).
	public lost = false;
	public done: { msg: string; count: StrongboxCountView } | null = null;
	// Coffre vide permis : 0 compté est un inventaire.
	public amount = new FormControl<number | null>(null, [Validators.required, nonNegativeCents]);
	public form = new FormGroup({ amount: this.amount });
	readonly counted = new CountedAmount(this.amount, { amount: 'strongbox-count-amount', submit: 'strongbox-count-submit' });

	// Focus sur la première case, après le focus d'ouverture de la boîte : une Entrée ne tombe pas sur la croix.
	@ViewChild(CashCountGridComponent) set gridRef(g: CashCountGridComponent | undefined) {
		this.counted.setGrid(g, this.dialogRef.afterOpened(), this.destroyRef);
	}

	get strongboxName(): string {
		return this.data.strongbox.station_name ?? 'Coffre ' + this.data.strongbox.station;
	}

	get storeName(): string {
		return this.data.strongbox.store_name ?? 'Magasin ' + this.data.strongbox.store;
	}

	// Date de l'inventaire qui ancre le solde affiché (anchor_start, même lecture que son compté), sinon celle de /safes ; l'auteur seulement s'il s'agit du même inventaire.
	get lastCount(): string {
		const at = this.balance?.anchor_start ?? this.listed?.last_count_at ?? null;
		const t = parseTz(at);
		if (!t) return '';
		const same = parseTz(this.listed?.last_count_at)?.valueOf() === t.valueOf();
		return 'le ' + countStamp(at, same ? this.listed?.last_count_user_name : null);
	}

	ngOnInit(): void {
		this.loadBalance();
	}

	// Réservé à l'habilitation Coffres-forts côté serveur (403 sans elle) : le bouton n'est montré qu'avec elle.
	loadBalance(): void {
		this.loadError = '';
		const { station, store } = this.data.strongbox;
		forkJoin([this.reglementService.getBalance$(station), this.reglementService.getSafes$(store)])
			.pipe(this.spin.block('Lecture du solde du coffre…'), takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: ([b, l]) => { this.balance = b; this.listed = l.safes.find((x) => x.station === station) ?? null; },
				error: (e) => this.loadError = refusalText(e),
			});
	}

	// Compté − solde théorique, au centime ; null tant que l'un des deux manque.
	get gap(): number | null {
		if (!this.balance || this.amount.invalid || this.counted.invalid) return null;
		return sumCents([Number(this.amount.value), -this.balance.balance]);
	}

	readonly signed = signedMoney;

	get canConfirm(): boolean {
		return this.amount.valid && !this.counted.invalid && !this.sending && !this.done && !this.lost;
	}

	confirm(): void {
		if (!this.canConfirm || this.spin.isLoading) return;
		const amount = Number(this.amount.value);
		this.sending = true;
		this.sendError = '';
		this.dialogRef.disableClose = true;	// Échap couperait le POST et cacherait un inventaire peut-être enregistré
		this.reglementService.postStrongboxCount$(this.data.strongbox.station, amount)
			.pipe(this.spin.block('Enregistrement de l\'inventaire…'), finalize(() => { this.sending = false; this.dialogRef.disableClose = this.lost; }), takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (r) => this.done = { msg: r.msg || 'Inventaire enregistré.', count: r.data },
				error: (e) => {
					this.lost = lostReply(e);
					this.sendError = this.lost
						? `Réponse perdue (${lostCause(e)}) : l'inventaire de ${this.fc.transform(amount)} € du ${this.strongboxName} a peut-être été enregistré. Fermez cette fenêtre : s'il est passé, la carte « Coffres-forts » montrera son dernier inventaire à l'heure d'aujourd'hui. Ne le refaites pas avant.`
						: refusalText(e);
				},
			});
	}

	// Jamais pendant l'envoi : fermer détruirait la boîte et couperait le POST, inventaire peut-être enregistré.
	close(): void {
		if (this.sending) return;
		if (this.lost) this.reglementService.markUncertain('strongbox-count');
		this.dialogRef.close(this.done?.count ?? null);
	}
}
